import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { registerSW } from 'virtual:pwa-register'
import App from './App.vue'
import { router } from './router'
import { i18n, setLocale, t } from './i18n'
import { useSettingsStore } from './stores/settings'
import { requestPersistentStorage } from './db/storage-quota'
import { useToaster } from './composables/useToaster'
import { installGlobalErrorCapture, vueErrorHandler } from './db/error-log'
import { vReveal } from './composables/vReveal'
import './styles/main.css'

async function bootstrap() {
  // Instalar captura de errores global cuanto antes (antes de montar Vue).
  installGlobalErrorCapture()

  const app = createApp(App)
  const pinia = createPinia()

  // Reportar errores de componentes Vue al log local.
  app.config.errorHandler = vueErrorHandler

  app.use(pinia)

  // Cargar preferencias antes de montar para evitar parpadeo de tema/idioma.
  const settings = useSettingsStore()
  await settings.init()
  setLocale(settings.settings.locale)

  app.use(i18n)

  // Restaurar sesión de Supabase si el sync está configurado.
  // IMPORTANTE (LCP): NO hacemos `await` aquí. Antes este `await` bloqueaba
  // `app.mount()` con un round-trip de red (getSession + loadProfile a
  // Supabase) + la descarga del chunk vendor-sync (286 KB), retrasando el
  // primer paint varios segundos (LCP medido: 12.9s). Ahora montamos la app
  // inmediatamente y arrancamos auth en background; App.vue reacciona al
  // cambio de `auth.isAuthenticated` cuando la sesión se restaura.
  app.use(router)
  app.directive('reveal', vReveal)
  app.mount('#app')

  if (settings.settings.sync.supabaseUrl && settings.settings.sync.supabaseAnonKey) {
    // Fire-and-forget: el auth store expone `initializing=true` mientras
    // carga, y App.vue/LibraryView muestran un splash hasta que termine.
    void import('./sync/auth').then(({ useAuthStore }) => {
      void useAuthStore().init()
    })

    // Pipeline de eventos para stats globales (panel admin). Offline-first:
    // encola en IndexedDB y flushea en batches. Sin sesión no hace nada.
    void import('./composables/useEventTracker').then(({ startEventTracker }) => {
      startEventTracker()
    })

    // Heartbeat de presencia: actualiza last_seen_at cada ~5 min para que
    // el panel admin pueda calcular DAU/WAU/MAU. Carga diferida del store.
    void import('./sync/auth').then(({ useAuthStore }) => {
      setInterval(() => void useAuthStore().touchLastSeen(), 5 * 60 * 1000)
    })
  }

  // Service Worker: autoUpdate + feedback al usuario.
  // - offlineReady: la app ya está cacheada y sirve sin red.
  // - needRefresh: hay una versión nueva; ofrecer recargar.
  const toast = useToaster()
  registerSW({
    onNeedRefresh() {
      toast.info(t('pwa.updateAvailable'), 0, {
        label: t('pwa.reload'),
        onClick: () => {
          window.location.reload()
        },
      })
    },
    onOfflineReady() {
      toast.success(t('pwa.offlineReady'))
    },
  })

  // Almacenamiento persistente: evitar que el navegador expulse
  // IndexedDB bajo presión (evictable). storage-quota ya está en el
  // grafo principal (vía repositories.ts), así que el import es estático
  // y no suma peso extra al bundle inicial.
  void requestPersistentStorage()

  // Escuchar cambios del sistema si el modo es "system".
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => useSettingsStore().applyAppMode())
}

bootstrap()
